import { DropdownField, MultiSelectField } from 'components/forms/EntityFormTypes';
import partition from 'lodash/partition';
import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { SelectType } from 'types/forms';
import { t } from './getTranslations';

export const useAutocompleteGrouping = <FormDataType>(
    field: DropdownField<FormDataType> | MultiSelectField<FormDataType>
) => {
    const optionsGroupsCount = new Set(field.options.map((item) => item.group)).size;
    const hasGroupedOptions = field.options.filter((option) => option.group !== undefined).length > 0;

    // if at least one option has a group, the groups will get sorted by the order set in the groupSortOrder array.
    // if groupSortOrder is missing a group value the option will be sorted alphabetically, but after everything defined in groupSortOrder.
    // if no groupSortOrder is defined, groups will be sorted alphabetically
    const optionsMaybeSorted = useMemo(() => {
        const result = field.options || [];
        if (!hasGroupedOptions) {
            return result;
        }
        // sort by given order
        if (field.groupSortOrder && field.groupSortOrder.length > 0) {
            const { groupSortOrder } = field;
            const [definedGroups, undefinedGroups] = partition(
                result,
                (option) => option.group && groupSortOrder.includes(option.group)
            );

            const sortedDefinedGroupsOptions = definedGroups.sort((a, b) => {
                const groupSortOrderA = groupSortOrder.findIndex((item) => item === a.group);
                const groupSortOrderB = groupSortOrder.findIndex((item) => item === b.group);
                return groupSortOrderA - groupSortOrderB;
            });

            const sortedUndefinedGroupsOptions = undefinedGroups.sort((a, b) => {
                if (!a.group || !b.group) {
                    return 1;
                }
                return a.group.localeCompare(b.group);
            });
            return [...sortedDefinedGroupsOptions, ...sortedUndefinedGroupsOptions];
        }
        // otherwise sort alphabetically by group name (to avoid duplicate groups)
        // BUT unspecified groups (null, undefined) will still be sorted at the end (For example group called "Other")
        return result.sort((a, b) => {
            if (!a.group || !b.group) {
                return 1;
            }
            return -String(b.group).localeCompare(String(a.group));
        });
    }, [field, hasGroupedOptions]);

    // if at least one option has a group, the following method will be called
    // options will be grouped by their group name or if none exists, "Other" will be provided as a fallback
    const handleGroupBy = (option: SelectType, defaultGroup = 'field.other'): string => {
        return option.group || t(defaultGroup);
    };

    return { hasGroupedOptions, optionsGroupsCount, optionsMaybeSorted, handleGroupBy };
};

export const useAutocompleteGroupingV2 = <TOption extends FieldValues>(
    options: TOption[],
    groupSortOrder?: string[] | undefined
) => {
    const optionsGroupsCount = new Set(options.map((item) => item.group)).size;
    const hasGroupedOptions = !!options.find((option) => option.group !== undefined);

    // if at least one option has a group, the groups will get sorted by the order set in the groupSortOrder array.
    // if groupSortOrder is missing a group value the option will be sorted alphabetically, but after everything defined in groupSortOrder.
    // if no groupSortOrder is defined, groups will be sorted alphabetically
    const optionsMaybeSorted = useMemo(() => {
        const result = options || [];
        if (!hasGroupedOptions) {
            return result;
        }
        // sort by given order
        if (groupSortOrder && groupSortOrder.length > 0) {
            const [definedGroups, undefinedGroups] = partition(
                result,
                (option) => option.group && groupSortOrder.includes(option.group)
            );

            const sortedDefinedGroupsOptions = definedGroups.sort((a, b) => {
                const groupSortOrderA = groupSortOrder.findIndex((item) => item === a.group);
                const groupSortOrderB = groupSortOrder.findIndex((item) => item === b.group);
                return groupSortOrderA - groupSortOrderB;
            });

            const sortedUndefinedGroupsOptions = undefinedGroups.sort((a, b) => {
                if (!a.group || !b.group) {
                    return 1;
                }
                return a.group.localeCompare(b.group);
            });

            return [...sortedDefinedGroupsOptions, ...sortedUndefinedGroupsOptions];
        }
        // otherwise sort alphabetically by group name (to avoid duplicate groups)
        // BUT unspecified groups (null, undefined) will still be sorted at the end (For example group called "Other")
        return result.sort((a, b) => {
            if (!a.group || !b.group) {
                return 1;
            }
            return -String(b.group).localeCompare(String(a.group));
        });
    }, [options, groupSortOrder, hasGroupedOptions]);

    return { hasGroupedOptions, optionsGroupsCount, optionsMaybeSorted };
};

/** One group of options as the combobox consumes them: a heading plus the rows under it. */
/** `key` is unique per group; `value` is not, since a name can head two separate runs. */
export type OptionGroup<TOption> = { key: string; value: string; items: TOption[] };

/**
 * Collapses an already-ordered option list into consecutive runs sharing a group name. Adjacency is
 * the point: callers hand over a list the sort (or the server's `ORDER BY`) has already grouped, so
 * a name that reappears after a different one opens a second heading instead of merging backwards.
 */
export const groupConsecutiveOptions = <TOption>(
    options: readonly TOption[],
    getGroupName: (option: TOption) => string
): OptionGroup<TOption>[] => {
    const groups: OptionGroup<TOption>[] = [];
    for (const option of options) {
        const groupName = getGroupName(option);
        const last = groups.at(-1);
        if (last && last.value === groupName) {
            last.items.push(option);
        } else {
            groups.push({ key: `${groups.length}:${groupName}`, value: groupName, items: [option] });
        }
    }
    return groups;
};
